/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { StackLayout } from '@euler/app/components/StackLayout';
import { CalendarIcon } from '@euler/app/components/icons/CalendarIcon';
import { ImageIcon } from '@euler/app/components/icons/ImageIcon';
import { ImagesIcon } from '@euler/app/components/icons/ImagesIcon';
import { QrcodeIcon } from '@euler/app/components/icons/QrcodeIcon';
import { VideoReelIcon } from '@euler/app/components/icons/VideoReelIcon';
import { VideosIcon } from '@euler/app/components/icons/VideosIcon';
import { IconProps } from '@euler/app/components/icons/types';
import { usePreviewImage } from '@euler/app/components/media/ImageViewerContext';
import { MediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { Typography } from '@euler/app/components/typography';
import { ExtendedPropertyAssetsUpload } from '@euler/app/flows/order/pages/OrderScreen/form/components/ExtendedPropertyAssetsUpload';
import { useTextInputRow } from '@euler/app/flows/order/pages/OrderScreen/functions/useTextInputRow';
import { ExtendedPropertyValidationErrorInfo } from '@euler/app/flows/order/types';
import { Center } from '@euler/components/Center';
import { Colors, StatusColors } from '@euler/components/Colors';
import { DateTimePicker } from '@euler/components/DateTimePicker';
import { Icon } from '@euler/components/Icomoon';
import { ItemProps } from '@euler/components/TableView';
import { ErrorText, Input } from '@euler/components/form/Input';
import { useShowFloatingTextInput } from '@euler/components/input/FloatingTextInput';
import { HStack } from '@euler/components/layout/Stack';
import {
  kDefaultDateFormat,
  kDefaultDatetimeFormat,
} from '@euler/functions/order/extendedPropertyUtil';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useLayoutContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';
import {
  ExtendedPropertyAssetInfo,
  ExtendedPropertyDef,
  ExtendedPropertyExtra,
  MediaObject,
  Option,
} from '@euler/model';
import { isNotNull, onErrorIgnore } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { withErrorHandler } from '@euler/utils/withErrorHandler';
import { useActionSheet } from '@expo/react-native-action-sheet';
import format from 'date-fns/format';
import { FC, memo, useMemo, useState } from 'react';
import {
  InteractionManager,
  Platform,
  TouchableOpacity,
  View,
} from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { useExtendedPropertyContext } from '../../components/ExtendedPropertyOptionModal';
import { Cell } from '../components/Cell';
import { IconWrapper } from '../components/IconWrapper';
import InlineRadios from '../components/InlineRadios';
import { Row } from '../components/Row';
import { extendedPropertyAnchorKey } from '../field-error-focus/anchorKeys';

export const ExtendedPropertyRow = memo(
  ({
    property,
    required,
    ...props
  }: ItemProps & { property: ExtendedPropertyDef; required?: boolean }) => {
    const { id, type } = property;

    const {
      theme,
      orderDetail$,
      updateDetail: internalUpdateDetail,
      draftId,
      formController,
    } = useOrderFlowContext();

    const navigation = useAppNavigation();
    const { rpx } = useResponsiveContext();
    const { iconColor, placeholderColor } = theme;

    const { property$ } = useExtendedPropertyContext();
    const [orderDetail] = useBehaviorSubject(orderDetail$);

    const { inputRef, inputStyle, onCellPress } = useTextInputRow();
    const width = useLayoutContainerWidth() - rpx(15) * 2;

    const error = formController.form.extendedProperties.error;
    const errorMsg =
      error != null && error instanceof ExtendedPropertyValidationErrorInfo
        ? error.errors[property.id]
        : undefined;

    const prop = orderDetail.extendedProps?.find(x => x.id === id);
    const value = prop?.value;

    const { showActionSheetWithOptions } = useActionSheet();
    const [showDateTimePicker, setShowDateTimePicker] = useState(false);

    const updateDetail: typeof internalUpdateDetail = recipe => {
      internalUpdateDetail(recipe);
      if (property.required) {
        void InteractionManager.runAfterInteractions(() => {
          formController.form.extendedProperties.validate();
        });
      }
    };

    const onPress = usePersistFn(() => {
      if (type === 'text' || type === 'switch') {
        onCellPress();
      } else if (type === 'datetime') {
        setShowDateTimePicker(true);
      } else if (type === 'select') {
        // see if we need to use action sheet for selection
        if (
          !property.multi &&
          !property.remark &&
          (property.renderType === 'actionsheet' ||
            (property.options.length < 6 &&
              property.options.every(x => x.label.length <= 20)))
        ) {
          showActionSheetWithOptions(
            {
              title: property.name,
              message: property.description || undefined,
              options: [...property.options.map(x => x.label), '关闭'],
              cancelButtonIndex: property.options.length,
            },
            index => {
              if (
                index == null ||
                index === -1 ||
                index === property.options.length
              ) {
                return;
              }
              const val =
                property.options[index].value ||
                property.options[index].defaultValue;

              updateDetail(draft => {
                const target = draft.extendedProps?.find(x => x.id === id);
                if (!target) {
                  if (!draft.extendedProps) {
                    draft.extendedProps = [];
                  }
                  draft.extendedProps.push({
                    id,
                    value: val,
                  });
                } else {
                  target.value = val;
                }
              });
            },
          );
        } else {
          property$.next(property);
        }
      }
    });

    const onTextValueChange = usePersistFn((text: string) => {
      updateDetail(draft => {
        const target = draft.extendedProps?.find(x => x.id === id);
        if (!target) {
          if (!draft.extendedProps) {
            draft.extendedProps = [];
          }
          draft.extendedProps.push({ id, value: text });
        } else {
          target.value = text;
        }
      });
    });

    const selectedPropertyOptions = useMemo(() => {
      if (type !== 'select' || typeof value === 'boolean' || !value) return [];
      const list = typeof value === 'string' ? [value] : value;
      return list
        .map(v => property.options.find(x => (x.value ?? x.defaultValue) === v))
        .filter(isNotNull);
    }, [property, type, value]);

    const selectPropertyValueText = useMemo(() => {
      if (type !== 'select' || typeof value === 'boolean') return '';
      if (!value) return '';
      const list = typeof value === 'string' ? [value] : value;
      return list
        .map(
          v =>
            property.options.find(x => (x.value ?? x.defaultValue) === v)
              ?.label,
        )
        .join(',');
    }, [property, type, value]);

    const showTextInput =
      type !== 'assets' &&
      (type === 'text' ||
        type == 'datetime' ||
        selectPropertyValueText.length < 10);

    const dateTimeDisplayText =
      type === 'datetime' && value
        ? withErrorHandler(() => {
            if (typeof value !== 'string') return undefined;
            const dateValue = dateFromValue(value);
            if (dateValue == null) return '';
            const formatString = property.displayFormat?.trim();
            return format(
              dateValue,
              formatString ||
                (property.dateOnly
                  ? kDefaultDateFormat
                  : kDefaultDatetimeFormat),
            );
          }, onErrorIgnore) ?? ''
        : '';

    const inputValue =
      type === 'text'
        ? typeof value === 'string'
          ? value
          : ''
        : type === 'datetime'
        ? dateTimeDisplayText
        : selectPropertyValueText;

    const switchOptions = useMemo<Option<boolean>[]>(() => {
      if (type !== 'switch') return [];
      return [
        { text: property.yesLabel?.trim() || '是', value: true },
        {
          text: property.noLabel?.trim() || '否',
          value: false,
        },
      ];
    }, [property, type]);

    const onSwitchChange = usePersistFn((on: boolean) => {
      if (type !== 'switch') return;
      updateDetail(draft => {
        const target = draft.extendedProps?.find(x => x.id === id);
        if (!target) {
          if (!draft.extendedProps) {
            draft.extendedProps = [];
          }
          draft.extendedProps.push({ id, value: on });
        } else {
          target.value = on;
        }
      });
    });

    const updateRemark = usePersistFn((text: string, defaultValue: any) => {
      updateDetail(draft => {
        const target = draft.extendedProps?.find(x => x.id === id);
        if (!target) {
          if (!draft.extendedProps) {
            draft.extendedProps = [];
          }
          draft.extendedProps.push({
            id,
            value: defaultValue,
            remark: text,
          });
        } else {
          target.remark = text;
        }
      });
    });

    const onSwitchRemarkChange = usePersistFn((text: string) => {
      if (type !== 'switch') return;
      updateRemark(text, property.defaultOn ?? false);
    });

    const onDateTimeValueChange = usePersistFn((date: Date) => {
      updateDetail(draft => {
        const target = draft.extendedProps?.find(x => x.id === id);
        if (!target) {
          if (!draft.extendedProps) {
            draft.extendedProps = [];
          }
          draft.extendedProps.push({ id, value: date.toISOString() });
        } else {
          target.value = date.toISOString();
        }
      });
      setShowDateTimePicker(false);
    });

    const onAssetsValueChange = usePersistFn(
      (assets: ExtendedPropertyAssetInfo[]) => {
        updateDetail(draft => {
          const urls = assets.map(x => x.url);
          const extra: ExtendedPropertyExtra = {
            type: 'assets',
            content: assets,
          };
          const target = draft.extendedProps?.find(x => x.id === id);
          if (!target) {
            if (!draft.extendedProps) {
              draft.extendedProps = [];
            }
            draft.extendedProps.push({ id, value: urls, extra });
          } else {
            target.value = urls;
            target.extra = extra;
          }
        });
      },
    );

    const showFloatingTextInput = useShowFloatingTextInput();
    const onEditRemarkPress = usePersistFn(() => {
      showFloatingTextInput({
        value: prop?.remark?.trim() ?? '',
        onDone: text => {
          updateRemark(text, undefined);
        },
        placeholder: `${property.name}备注`,
      });
    });

    const onlyAttachmentType =
      property.attachments?.length === 1
        ? property.attachments[0].type
        : undefined;

    const [showAttachments, setShowAttachments] = useState(false);

    const attachmentMedias = useMemo<MediaObject[]>(() => {
      const medias: MediaObject[] = [];
      if (onlyAttachmentType == null) {
        const videos =
          property.attachments?.filter(x => x.type === 'video') ?? [];
        if (
          property.attachments != null &&
          videos.length > 0 &&
          videos.length <= property.attachments.length
        ) {
          for (const attachment of property.attachments) {
            medias.push({
              id: attachment.id,
              type: attachment.type === 'video' ? 'video' : 'image',
              url: attachment.content,
            });
          }
        }
      }
      return medias;
    }, [onlyAttachmentType, property.attachments]);

    const previewImages = usePreviewImage();

    const onInfoButtonPress = usePersistFn(() => {
      if (!property.attachments?.length) return;
      if (property.attachments.every(x => x.type !== 'video')) {
        previewImages({
          imageUrls: property.attachments.map(x => x.content),
        });
      } else if (onlyAttachmentType === 'video') {
        navigation.navigate('_mediaPreview', {
          type: 'video',
          url: property.attachments[0].content,
          shareable: false,
        });
      } else {
        setShowAttachments(x => !x);
      }
    });

    let InfoIconComponent: FC<IconProps> | undefined = undefined;
    if (property.attachments?.length) {
      if (onlyAttachmentType === 'qrcode') {
        InfoIconComponent = QrcodeIcon;
      } else if (onlyAttachmentType === 'image') {
        InfoIconComponent = ImageIcon;
      } else if (onlyAttachmentType === 'video') {
        InfoIconComponent = VideoReelIcon;
      } else if (property.attachments.every(x => x.type === 'video')) {
        InfoIconComponent = VideosIcon;
      } else {
        InfoIconComponent = ImagesIcon;
      }
    }

    const infoButton = InfoIconComponent ? (
      <Center.Touchable
        ml={rpx(15)}
        onPress={onInfoButtonPress}
        hitSlop={PredefinedHitSlops.extraLarge}
        style={
          showAttachments
            ? {
                backgroundColor: StatusColors.Info,
                width: rpx(20),
                height: rpx(20),
                borderRadius: rpx(6),
              }
            : undefined
        }
      >
        <InfoIconComponent
          size={rpx(showAttachments ? 14 : 20)}
          fill={showAttachments ? 'white' : StatusColors.Info}
        />
      </Center.Touchable>
    ) : null;

    return (
      <Row
        {...props}
        errorProp={extendedPropertyAnchorKey(property.id)}
        detailIcon={
          type === 'select' || type === 'datetime' ? 'disclosure' : undefined
        }
        title={type === 'switch' ? property.name : undefined}
        required={type === 'switch' ? required : undefined}
        note={
          type === 'switch'
            ? property.description?.trim() || undefined
            : undefined
        }
        isSwitch={
          type === 'switch' &&
          !property.yesLabel?.trim() &&
          !property.noLabel?.trim()
        }
        switchOn={
          type === 'switch' &&
          (value === true || (prop == null && property.defaultOn))
        }
        switchDisabled={props.disabled}
        onSwitchChange={onSwitchChange}
        detail={
          (type === 'switch' || infoButton != null) && (
            <>
              {type === 'switch' &&
              (property.yesLabel?.trim() || property.noLabel?.trim()) ? (
                <InlineRadios
                  options={switchOptions}
                  disabled={props.disabled}
                  value={
                    prop?.value == null &&
                    property.type === 'switch' &&
                    property.defaultOn
                      ? true
                      : prop?.value === true
                  }
                  css={`
                    flex-wrap: nowrap;
                    align-content: flex-start;
                    max-width: ${width * 0.5}px;
                    margin-left: ${rpx(8)}px;
                  `}
                  onChange={onSwitchChange}
                />
              ) : undefined}
              {infoButton}
            </>
          )
        }
        onPress={onPress}
        icon={
          <IconWrapper>
            {type === 'datetime' ? (
              <CalendarIcon fill={iconColor} size={rpx(22)} />
            ) : (
              <RowIcon color={iconColor} />
            )}
          </IconWrapper>
        }
        extra={
          attachmentMedias.length && showAttachments ? (
            <StackLayout direction="row">
              <MediaCarousel
                display="list"
                listKey={property.id}
                numberOfItemsPerRow={4}
                itemSpacer={rpx(6)}
                medias={attachmentMedias}
                listWidth={width - rpx(114)}
                itemStyle={{
                  borderRadius: rpx(5),
                  overflow: 'hidden',
                  marginBottom: rpx(5),
                }}
                playIconSize={rpx(16)}
                noPlaceholder={media =>
                  media.type === 'image' && media.url?.includes('#')
                }
                css={`
                  margin-top: ${rpx(10)}px;
                  margin-bottom: ${rpx(10)}px;
                  margin-right: ${rpx(5)}px;
                  padding-left: ${rpx(55)}px;
                `}
              />
            </StackLayout>
          ) : undefined
        }
      >
        {type === 'switch' ? (
          property.remark ? (
            <Cell label="" style={{ marginTop: rpx(4) }}>
              <Input
                inputRef={inputRef}
                style={inputStyle}
                placeholderTextColor={placeholderColor}
                placeholder={'备注'}
                value={prop?.remark ?? ''}
                editable={!props.disabled}
                onChangeText={onSwitchRemarkChange}
              />
            </Cell>
          ) : null
        ) : (
          <Cell
            label={property.name}
            helpText={type === 'assets' ? undefined : property.description}
            labelDecoration={
              type === 'assets'
                ? `(不超过${property.limit ?? 10}个)`
                : undefined
            }
            helpTextIconHidden
            required={required}
          >
            {showTextInput && (
              <Input
                inputRef={inputRef}
                style={inputStyle}
                placeholderTextColor={placeholderColor}
                placeholder={
                  property.placeholder ??
                  (type === 'text' ? '请输入' : '请选择')
                }
                value={inputValue}
                onChangeText={onTextValueChange}
                editable={type === 'text' && !props.disabled}
                pointerEvents="none"
              />
            )}
            {type === 'select' &&
              !showTextInput &&
              selectedPropertyOptions.map(option => (
                <View key={option.id}>
                  <Typography.Label
                    regular
                    color="#777"
                    size={rpx(13)}
                    lineHeight={rpx(18)}
                    numberOfLines={1}
                    css={`
                      margin: ${rpx(2)}px 0;
                      transform: translateX(${rpx(-4)}px);
                    `}
                  >
                    {`•${option.label}`}
                  </Typography.Label>
                </View>
              ))}
            {type === 'select' && property.remark && prop?.remark ? (
              <Typography.Label
                light
                color={StatusColors.Success}
                size={rpx(12)}
                lineHeight={rpx(16)}
                css={`
                  margin-top: ${rpx(5)}px;
                `}
              >{`备注: ${prop.remark}`}</Typography.Label>
            ) : null}
            {type === 'datetime' && property.remark ? (
              <HStack
                justifyContent="flex-start"
                alignItems="center"
                css={`
                  margin-top: ${rpx(5)}px;
                `}
              >
                <Typography.Label
                  light
                  color={StatusColors.Success}
                  size={rpx(12)}
                  lineHeight={rpx(16)}
                >{`备注: ${prop?.remark?.trim() || '--'}`}</Typography.Label>
                {!props.disabled && (
                  <TouchableOpacity
                    hitSlop={PredefinedHitSlops.large}
                    onPress={onEditRemarkPress}
                    css={`
                      margin-left: ${rpx(8)}px;
                    `}
                  >
                    <Icon name="pencil" size={rpx(10)} color={Colors.Gray3} />
                  </TouchableOpacity>
                )}
              </HStack>
            ) : null}
            {type === 'assets' && (
              <ExtendedPropertyAssetsUpload
                key={draftId}
                width={width - rpx(100)}
                property={property}
                helpText={property.description}
                readonly={props.disabled}
                defaultValue={
                  prop?.extra?.type === 'assets'
                    ? prop.extra.content
                    : undefined
                }
                onChange={onAssetsValueChange}
              />
            )}
          </Cell>
        )}
        {type === 'datetime' && (
          <DateTimePicker
            isVisible={showDateTimePicker}
            onCancel={() => setShowDateTimePicker(false)}
            onConfirm={onDateTimeValueChange}
            title={property.name}
            confirmTextIOS="确认"
            cancelTextIOS="关闭"
            mode={
              // todo(eric): handle android datetime picker later
              Platform.OS === 'android' || property.dateOnly
                ? 'date'
                : 'datetime'
            }
            date={dateFromValue(
              (value && Array.isArray(value)) || typeof value === 'boolean'
                ? undefined
                : value,
            )}
          />
        )}
        {errorMsg ? (
          <ErrorText
            errorMsg={errorMsg}
            css={`
              margin-left: ${rpx(10)}px;
              margin-top: ${rpx(10)}px;
            `}
          />
        ) : undefined}
      </Row>
    );
  },
);

export const RowIcon = memo(
  ({ size, color = '#207FE7', style, ...props }: IconProps) => {
    const { rpx } = useResponsiveContext();
    return (
      <Svg
        viewBox="0 0 1024 1024"
        width={size ?? rpx(20)}
        height={size ?? rpx(20)}
        style={style}
        fill="none"
        {...props}
      >
        <Path
          d="M302.336 504.768l-146.944-88.064a24.64 24.64 0 0 0-34.048 8.448 24.992 24.992 0 0 0 8.448 34.304l146.944 88.064a24.512 24.512 0 0 0 34.048-8.704 24.64 24.64 0 0 0-8.448-34.048z m156.672 516.096c5.888 1.792 12.544 1.28 18.688-2.304a24.96 24.96 0 0 1-18.688 2.304zM302.336 504.768l-146.944-88.064a24.64 24.64 0 0 0-34.048 8.448 24.992 24.992 0 0 0 8.448 34.304l146.944 88.064a24.512 24.512 0 0 0 34.048-8.704 24.64 24.64 0 0 0-8.448-34.048z m0 0l-146.944-88.064a24.64 24.64 0 0 0-34.048 8.448 24.992 24.992 0 0 0 8.448 34.304l146.944 88.064a24.512 24.512 0 0 0 34.048-8.704 24.64 24.64 0 0 0-8.448-34.048z m0 0l-146.944-88.064a24.64 24.64 0 0 0-34.048 8.448 24.992 24.992 0 0 0 8.448 34.304l146.944 88.064a24.512 24.512 0 0 0 34.048-8.704 24.64 24.64 0 0 0-8.448-34.048z m0 0l-146.944-88.064a24.64 24.64 0 0 0-34.048 8.448 24.992 24.992 0 0 0 8.448 34.304l146.944 88.064a24.512 24.512 0 0 0 34.048-8.704 24.64 24.64 0 0 0-8.448-34.048z m0 0l-146.944-88.064a24.64 24.64 0 0 0-34.048 8.448 24.992 24.992 0 0 0 8.448 34.304l146.944 88.064a24.512 24.512 0 0 0 34.048-8.704 24.64 24.64 0 0 0-8.448-34.048z m175.36 513.792a23.264 23.264 0 0 1-18.688 2.304 24.64 24.64 0 0 0 18.688-2.304z m379.584-49.888a25.6 25.6 0 0 1-25.6-25.6v-192.192c0-6.976 2.88-13.728 7.968-18.56l127.616-121.376a21.6 21.6 0 0 0-15.712-36.352h-309.792a21.6 21.6 0 0 0-15.136 36.928l117.728 121.792c4.64 4.8 7.2 11.168 7.2 17.792v141.44a25.6 25.6 0 1 1-51.2 0v-131.104l-110.24-114.016a72.256 72.256 0 0 1-21.152-51.264 72.864 72.864 0 0 1 72.8-72.768h309.824a72.864 72.864 0 0 1 72.8 72.768 72.32 72.32 0 0 1-21.344 51.456l-120.128 114.24v181.184a25.6 25.6 0 0 1-25.632 25.6z m62.88-702.272c8-5.184 9.504-11.808 10.56-16.544a25.056 25.056 0 0 0-13.568-25.088L476.832 4.608a24.96 24.96 0 0 0-22.528 0L13.984 224.768c-16.384 10.752-13.824 34.56-13.824 37.12v469.76c0 8.704 4.608 16.896 12.032 21.504 0 0 430.848 260.352 438.272 263.68 15.104 7.424 24.576 5.376 31.744 1.792l143.36-82.944a25.568 25.568 0 0 0 9.216-35.072 25.568 25.568 0 0 0-34.816-9.472l-99.072 57.6v-0.256l-10.24 5.632V511.744L880.288 290.56l39.872-24.16z m-479.68 245.344v441.088l-390.4-235.264V290.048l390.4 221.696z m25.088-43.52L78.24 248.576 465.568 55.04l387.328 193.536-387.328 219.648z"
          fill={color}
        />
      </Svg>
    );
  },
);
